import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../../lib/cn";
import { Avatar } from "../avatar";
import { Badge } from "../badge";
import { Button } from "../button";
import { CodeBlock } from "../code";
import { StatusBadge, type CloudStatus } from "./status";

export interface DeploymentStepItem {
  id: string;
  name: string;
  status: CloudStatus;
  duration?: string;
  summary?: ReactNode;
  /** Logs bruts associés à cette étape (ex: erreurs de compilation). */
  logSnippet?: string;
}

export interface DeploymentTimelineProps extends Omit<ComponentPropsWithRef<"div">, "title"> {
  title?: ReactNode;
  commitHash?: string;
  branchName?: string;
  commitMessage?: string;
  authorName?: string;
  authorAvatar?: string;
  steps: DeploymentStepItem[];
  /** Action d'annulation ou d'annuler/relancer. */
  onRollback?: () => void;
  onRebuild?: () => void;
}

/**
 * Pipeline de déploiement et historique de build pour console Cloud.
 *
 * ── Alignement & Composants propriétaires ─────────────────────────────────
 * - Alignement axe vertical / pastilles 100% au pixel près (center-to-center).
 * - Utilisation systématique des composants Button, Badge et CodeBlock.
 */
export function DeploymentTimeline({
  title = "Déploiement en cours",
  commitHash,
  branchName,
  commitMessage,
  authorName,
  authorAvatar,
  steps,
  onRollback,
  onRebuild,
  className,
  ...props
}: DeploymentTimelineProps) {
  const isFailed = steps.some((s) => s.status === "failed");

  return (
    <div
      className={cn(
        "flex flex-col gap-5 rounded-xl border border-border bg-surface-raised p-5 shadow-xs",
        className,
      )}
      {...props}
    >
      {/* En-tête de la Timeline */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-4">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-fg">{title}</h3>
            {branchName && <Badge tone="neutral">{branchName}</Badge>}
            {commitHash && (
              <Badge tone="brand" className="font-mono text-xs">
                #{commitHash.substring(0, 7)}
              </Badge>
            )}
          </div>
          {commitMessage && <p className="text-xs text-fg-muted mt-0.5">{commitMessage}</p>}
        </div>

        {/* Action Rebuild / Rollback via Button du design system */}
        <div className="flex items-center gap-2">
          {authorName && (
            <div className="mr-2 flex items-center gap-2 text-xs text-fg-muted">
              <Avatar src={authorAvatar} name={authorName} size="sm" />
              <span className="font-medium text-fg">{authorName}</span>
            </div>
          )}

          {isFailed && onRebuild && (
            <Button size="sm" variant="primary" onClick={onRebuild}>
              Relancer le build
            </Button>
          )}

          {onRollback && (
            <Button size="sm" variant="outline" onClick={onRollback}>
              Rollback
            </Button>
          )}
        </div>
      </div>

      {/* Liste verticale des étapes de la Timeline avec axe et pastilles alignés à 100% */}
      <div className="relative space-y-6 before:absolute before:left-3 before:top-2.5 before:bottom-2.5 before:w-0.5 before:-translate-x-1/2 before:bg-border">
        {steps.map((step) => {
          return (
            <div key={step.id} className="relative flex flex-col gap-1.5 pl-8">
              {/* Pastille de repère parfaitement centrée sur l'axe vertical à x=12px */}
              <span className="absolute left-3 top-0.5 flex size-5 -translate-x-1/2 items-center justify-center rounded-full bg-surface-raised ring-4 ring-surface-raised">
                <span
                  className={cn(
                    "size-2.5 rounded-full shrink-0",
                    step.status === "succeeded" && "bg-success-fg",
                    step.status === "failed" && "bg-danger-fg",
                    (step.status === "provisioning" || step.status === "updating") &&
                      "bg-accent-fg animate-ping",
                    step.status === "pending" && "bg-fg-subtle",
                  )}
                />
              </span>

              {/* Titre de l'étape & Statut */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <span className="text-sm font-semibold text-fg">{step.name}</span>
                  <StatusBadge status={step.status} />
                </div>

                {step.duration && (
                  <span className="font-mono text-xs text-fg-subtle tabular-nums">
                    {step.duration}
                  </span>
                )}
              </div>

              {step.summary && <div className="text-xs text-fg-muted">{step.summary}</div>}

              {/* Snippet d'erreur de build (si disponible) */}
              {step.logSnippet && (
                <div className="mt-1">
                  <CodeBlock>{step.logSnippet}</CodeBlock>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
